import React, { useEffect, useState } from 'react';

import {
  Box,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from '@mui/material';

import Button from 'components/Button';
import { useToast } from 'hooks/useToast';
import { apiRainmakersV2 } from 'services/apiService';
import { MeetingRoom } from 'types/meetingTypes';
import { fetchMeetingRooms } from 'utils/meetingUtils';

interface LocationTabProps {
  data: { location: string; is_online: boolean; room_id?: number };
  eventId: string;
  onNext: (updatedLocal: {
    local: { is_online: boolean; room_id?: number; location: string };
  }) => void;
  onCancel: () => void;
  isEditMode?: boolean;
}

interface MenuItemType {
  documentId?: string;
  code?: string;
  title?: string;
  label?: string;
  subMenu?: MenuItemType[];
  internal_id?: string | null;
}

function findMenuPathByRoomId(
  menu: MenuItemType[],
  roomId: string | number,
  path: string[] = []
): string[] | null {
  for (const item of menu) {
    if (
      item.documentId === String(roomId) ||
      item.code === String(roomId) ||
      item.internal_id === String(roomId)
    ) {
      return [...path, item.title || item.label || ''];
    }
    if (item.subMenu) {
      const found = findMenuPathByRoomId(item.subMenu, roomId, [
        ...path,
        item.title || item.label || '',
      ]);
      if (found) return found;
    }
  }
  return null;
}

const LocationTab: React.FC<LocationTabProps> = ({
  data,
  eventId,
  onNext,
  onCancel,
  isEditMode = false,
}) => {
  const [isOnline, setIsOnline] = useState<boolean | null>(data.is_online ?? null);
  const [location, setLocation] = useState<string>(data.location || '');
  const [selectedRoom, setSelectedRoom] = useState<string | null>(
    data.room_id ? String(data.room_id) : null
  );
  const [meetingRooms, setMeetingRooms] = useState<MeetingRoom[]>([]);
  const [loading, setLoading] = useState(true);

  const { toastError, toastSuccess } = useToast();

  useEffect(() => {
    const organizationId = localStorage.getItem('x-organization-uuid') || '';
    const userId = localStorage.getItem('user_id') || '';
    setLoading(true);
    fetchMeetingRooms(organizationId, userId)
      .then(rooms => setMeetingRooms(rooms))
      .catch(() => setMeetingRooms([]))
      .finally(() => setLoading(false));
  }, []);

  const handleSaveLocation = async () => {
    try {
      const payload = isOnline
        ? {
            is_online: true,
            location: '',
            room_id: selectedRoom ? Number(selectedRoom) : undefined,
          }
        : { is_online: false, location, room_id: null };

      if (isEditMode) {
        await apiRainmakersV2.patch(`/events/v1/local/${eventId}`, payload);
      } else {
        await apiRainmakersV2.post(`/events/v1/local/${eventId}`, payload);
      }

      toastSuccess('Local do evento salvo com sucesso!');
      onNext({
        local: {
          is_online: isOnline || false,
          room_id: selectedRoom ? Number(selectedRoom) : undefined,
          location,
        },
      });
    } catch (error) {
      toastError('Erro ao salvar o local do evento.');
    }
  };

  const isFormValid = () => {
    if (isOnline === true) {
      return selectedRoom !== null;
    }
    if (isOnline === false) {
      return location.trim().length > 0;
    }
    return false;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: '100%',
        minHeight: '28rem',
        gap: '1rem',
        padding: 0,
      }}
    >
      <RadioGroup
        name="isOnline"
        value={isOnline !== null ? String(isOnline) : ''}
        onChange={e => {
          const value = e.target.value === 'true';
          setIsOnline(value);
          if (!value) {
            setSelectedRoom(null);
          } else {
            setLocation('');
          }
        }}
        row
        sx={{ flexDirection: 'column' }}
      >
        <FormControlLabel
          value="false"
          control={
            <Radio
              sx={{
                color: 'primary',
                '&.Mui-checked': { color: 'primary' },
              }}
            />
          }
          label="Presencial"
        />
        <FormControlLabel
          value="true"
          control={
            <Radio
              sx={{
                color: 'primary',
                '&.Mui-checked': { color: 'primary' },
              }}
            />
          }
          label="Virtual"
        />
      </RadioGroup>

      {isOnline === false && (
        <TextField
          label="Insira aqui o endereço"
          value={location}
          onChange={e => setLocation(e.target.value)}
          fullWidth
        />
      )}

      {isOnline === true && (
        <Select
          value={selectedRoom ?? ''}
          onChange={e => setSelectedRoom(e.target.value)}
          displayEmpty
          fullWidth
          disabled={loading}
        >
          <MenuItem value="" disabled>
            {loading ? 'Carregando salas...' : 'Selecionar sala do evento'}
          </MenuItem>
          {meetingRooms.map(room => {
            let menu = [];
            try {
              menu = JSON.parse(localStorage.getItem('menuData') || '[]');
              console.log('[LocationTab] [menu]', menu);
              console.log('[LocationTab] [room]', room);
            } catch (error) {
              console.error('[LocationTab] Error parsing menu:', error);
            }
            const pathArr = findMenuPathByRoomId(menu, String(room.room_data.id));
            const label = pathArr ? pathArr.join(' | ') : room.room_data.title;
            console.log('[LocationTab] [room]', {
              id: room.room_data.id,
              internal_id: room.room_data.internal_id,
              label,
              pathArr,
            });
            return (
              <MenuItem key={room.room_data.id} value={String(room.room_data.id)}>
                {label}
              </MenuItem>
            );
          })}
        </Select>
      )}

      <Box display="flex" justifyContent="space-between" gap="25%">
        <Button variant="outline" color="secondary" onClick={onCancel} fullWidth>
          Cancelar
        </Button>
        <Button
          variant="primary"
          color="primary"
          onClick={handleSaveLocation}
          fullWidth
          disabled={!isFormValid()}
        >
          {isEditMode ? 'Salvar Alterações' : 'Agendamento'}
        </Button>
      </Box>
    </Box>
  );
};

export default LocationTab;
